import React from "react";
import type { DraftView } from "@/api";
import { Button, Input } from "@/ds";
import { useT } from "../../i18n";
import styles from "./email-review.module.css";

export function DraftRecipient({ draft, address, editor }: {
  draft: DraftView;
  address: string;
  editor?: {
    editing: boolean; value: string; valid: boolean; busy: boolean; focus: boolean;
    onStart: () => void;
    onChange: (value: string, valid: boolean) => void;
  };
}) {
  const t = useT();
  const controlRef = React.useRef<HTMLDivElement>(null);
  const errorId = React.useId();
  React.useEffect(() => {
    if (editor?.editing && editor.focus) controlRef.current?.querySelector("input")?.focus();
  }, [editor?.editing, editor?.focus]);
  const changed = editor?.editing
    ? editor.value !== draft.recipient?.default_address
    : Boolean(draft.recipient?.override_address);
  return <div ref={controlRef} className={styles.recipientControl}>
    <div className={styles.recipientRow}>
      {editor?.editing ? <Input type="email" required maxLength={254}
        autoComplete="off" spellCheck={false} aria-label={t("ws.recipient.address")}
        aria-invalid={!editor.valid} aria-describedby={!editor.valid ? errorId : undefined}
        disabled={editor.busy} value={editor.value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => editor.onChange(
          event.target.value, event.target.validity.valid && !/[\r\n\0]/.test(event.target.value),
        )} /> : editor ? <button type="button"
        className={`ribbon-field ${styles.recipientAddress}`}
        disabled={editor.busy} onClick={editor.onStart}
        aria-label={`${t("ws.recipient.change")}: ${address}`}>
        <span data-mail-address-content="to">{address}</span>
      </button> : <span data-mail-address-content="to">{address}</span>}
      {editor && changed && draft.recipient?.default_address && <Button
        variant="ghost" size="sm" disabled={editor.busy}
        onClick={() => { if (!editor.editing) editor.onStart(); editor.onChange(draft.recipient!.default_address!, true); }}>
        {t("ws.recipient.reset")}
      </Button>}
    </div>
    {changed && <span className={styles.recipientHint}>{t("ws.recipient.changed")}</span>}
    {editor?.editing && <span className={styles.recipientHint}>
      {t("ws.recipient.default", { address: draft.recipient?.default_address ?? "—" })}
    </span>}
    {editor?.editing && !editor.valid && <span id={errorId} role="alert" className={styles.recipientError}>
      {t("ws.recipient.invalid")}
    </span>}
  </div>;
}
